/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { IEventHubsSelectBindMetadata } from 'app/extensibility/metadatas/control/event-hubs-select-metadata';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { IValidatorMetadata } from 'app/extensibility/metadatas/validator/validator-metadata';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { GlobalVariables } from 'app/services/globals';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { streamingRunMode } from 'app/model/dataflow/dataset-settings/event-hubs-settings';

function getConnectionSettings(): ControlMetadata {
    const fields = [
        { name: 'namespaceName', label: stringResources.ModelName_EventHubs_Namespace_DisplayText, isSecure: false },
        { name: 'accessKeyName', label: stringResources.ModelName_EventHubs_PolicyName_DisplayText, isSecure: false },
        { name: 'accessKey', label: stringResources.ModelName_EventHubs_PolicyKey_DisplayText, isSecure: true }
    ];

    const eventHubsEndpointSettings: ControlMetadata = {
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AccountSelectionMethodLabel,
        description: stringResources.EventHubs_SelectMethod_Description,
        options: azureResourceSelectMethodOptions,
        defaultValue: '{!!namespaceName ? "manual" : "azure"}',
        isLabelVertical: true,
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'event-hubs-select',
                        isPEConfig: false,
                        onlyKafkaSupportedNS: true,
                        bind: fields.reduce((accum, cur) => { accum[cur.name] = cur.name; return accum; }, {} as IEventHubsSelectBindMetadata),
                        validators: fields.reduce((accum, cur) => {
                            accum[cur.name] = [{ type: 'required', errorMessage: '' }];
                            return accum;
                        }, {} as { [name: string]: IValidatorMetadata[] })
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: fields
                    .reduce((accum, entry) => {
                        const control: ControlMetadata = {
                            type: 'parameterizable',
                            child: {
                                type: entry.isSecure ? 'password' : 'textbox',
                                bind: entry.name,
                                label: entry.label,
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ],
                                isLabelVertical: true
                            },
                            disableAddParameter: entry.isSecure
                        };
                        accum.push(control);
                        return accum;
                    }, [] as ControlMetadata[])
            }
        ]
    };

    return eventHubsEndpointSettings;
}

function advancedSettings(isAutoInfer: boolean): ControlMetadata {

    const includeHeaders: ControlMetadata = {
        type: 'parameterizable',
        child: {
            type: 'checkbox',
            bind: 'includeHeaders',
            description: stringResources.IncludeHeadersToolTip,
            isDataFlowExpression: true,
            label: stringResources.IncludeHeader
        },
        isDataFlowExpression: true
    };

    const numSampleRecords: ControlMetadata = {
        type: 'parameterizable',
        hide: !isAutoInfer,
        child: {
            type: 'textbox',
            bind: 'numSampleRecords',
            isDataFlowExpression: true,
            required: true,
            defaultValue: 10000,
            inputType: 'number',
            description: stringResources.Num_Of_Infer_Records_Tooltip,
            label: stringResources.EventHubs_AutoInferRecords_Label
        },
        isDataFlowExpression: true
    };

    return {
        type: 'accordion',
        emitValueChange: false,
        header: stringResources.AdvancedSettings,
        children: isAutoInfer ? [includeHeaders, numSampleRecords] : [includeHeaders]
    };
}

function importFile(label: string): ControlMetadata {
    return {
        type: 'import-file-content',
        bind: 'payloadSchema',
        encodeBase64: true,
        label,
        required: true,
        importLabel: stringResources.Import,
        isDataFlowExpression: true
    };
}

const loadSelection: ControlMetadata = {
    type: 'checkbox-switch',
    bind: 'changeDataCapture',
    label: stringResources.ADFDataMapperChangeDataCaptureLabel,
    defaultValue: true,
    disabled: true,
    isDataFlowExpression: true,
    cases: [
        {
            caseValue: false,
            children: []
        },
        {
            caseValue: true,
            children: [
                        {
                            type: 'select-switch',
                            bind: 'runMode',
                            label: stringResources.DataFlowRunMode,
                            options: [
                                {
                                    label: stringResources.DataFlowIncrementalChangesOnly,
                                    value: streamingRunMode.Now
                                },
                                {
                                    label: stringResources.DataFlowFullOnFirstRunThenIncremental,
                                    value: streamingRunMode.Beginning
                                },
                                {
                                    label: stringResources.DataFlowStartFromTimestampThenIncremental,
                                    value: streamingRunMode.Timestamp
                                }
                            ],
                            cases: [
                                        {
                                            caseValue: streamingRunMode.Timestamp,
                                            children: [
                                                {
                                                    type: 'date-time-picker',
                                                    bind: 'seekToTimestamp',
                                                    styleOverrides: { width: '479px' },
                                                    label: stringResources.DataFlowColumnType_Timestamp,
                                                    isDataFlowExpression: true
                                                }
                                            ]
                                        }
                                    ]
                        },
                    ]

        }
    ]
};

const instanceSelection: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'radio-switch',
        bind: 'instanceSelectionType',
        label: stringResources.EventHubs_AddEventHubs_DisplayText,
        options: [
            {
                value: stringResources.InstanceSelectionType_Manual,
                label: stringResources.SelectManually
            },
            {
                value: stringResources.InstanceSelectionType_Pattern,
                label: stringResources.SetPattern
            }
        ],
        cases: [
            {
                caseValue: stringResources.InstanceSelectionType_Manual,
                children: [
                    {
                        type: 'event-hubs-instance-select',
                        bind: 'eventHubsName',
                        label: ' ',
                        allowMultiSelect: true,
                        isDataFlowExpression: true
                    }
                ]
            },
            {
                caseValue: stringResources.InstanceSelectionType_Pattern,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'eventHubsNamePattern',
                            placeholder: stringResources.EventHubs_Sender_Placeholder,
                            isDataFlowExpression: true
                        },
                        isDataFlowExpression: true
                    }
                ]
            }
        ],
        isDataFlowExpression: true
    },
    isDataFlowExpression: true
};

const schemaInferSelection: ControlMetadata = {
    type: 'checkbox-switch',
    bind: 'isAutoInfer',
    label: stringResources.Auto_Infer_Schema_DisplayText,
    description: stringResources.Infer_Schema_From_Source_Tooltip,
    defaultValue: true,
    cases: [
        {
            caseValue: false,
            children: [importFile(stringResources.DataFlowDebugSampleFile), advancedSettings(false)]
        },
        {
            caseValue: true,
            children: [advancedSettings(true)]
        }
    ],
    isDataFlowExpression: true
};

function formatSelection(isSource: boolean): ControlMetadata {

    const options = [
        {
            value: 'passthrough',
            label: stringResources.ModelName_BinaryDataset
        },
        {
            value: 'json',
            label: stringResources.ModelName_JsonDataset
        },
        {
            value: 'avro',
            label: stringResources.ModelName_AvroDataset
        }
    ];
    if (isSource) {
        options.push({
            value: 'delimited',
            label: stringResources.ModelName_DelimitedTextDataset
        });
    }

    return {
        type: 'parameterizable',
        child: {
            type: 'select-switch',
            bind: 'format',
            label: stringResources.DataFormat_Label,
            options,
            cases: [
                {
                    caseValue: 'passthrough',
                    children: [advancedSettings(false)]
                },
                {
                    caseValue: 'avro',
                    children: [importFile(stringResources.Import_Schema_File_DisplayText), advancedSettings(false)]
                },
                {
                    caseValue: 'json',
                    children: [schemaInferSelection]
                },
                {
                    caseValue: 'delimited',
                    children: [schemaInferSelection]
                }
            ],
            isDataFlowExpression: true
        },
        isDataFlowExpression: true
    };
}

export const eventHubsMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.eventHubs.linkedServiceType,
    displayName: GlobalVariables.enableEventHubsForApacheKafka ? stringResources.EventHubsForApacheKafka_DisplayText : stringResources.EventHubs_DisplayText,
    linkedServiceKind: 'unknown',
    icon: {
        className: 'svg-i-100x100-event_hubs'
    },
    category: ['Streaming'],
    isFirstClass: true,
    isCDCOnly: !GlobalVariables.enableEventHubsMDF,
    supportFirstClassParameterExperience: true,
    supportDataFlowSource: true,
    supportDataFlowSink: true,
    resourceTypes: ['Microsoft.EventHub/namespaces'],
    copySourceTypes: [],
    copySinkTypes: [],
    dataflowSource: [
        {
            type: DatasetFormatType.EventHubs,
            settings: adfModelTypeMap.eventHubs.dataflowSettings,
            controls: [
                instanceSelection,
                loadSelection,
                formatSelection(true)
            ],
            useInlineProjection: true
        }
    ],
    dataflowSink: [
        {
            type: DatasetFormatType.EventHubs,
            settings: adfModelTypeMap.eventHubs.dataflowSettings,
            controls: [
                {
                    type: 'event-hubs-instance-select',
                    bind: 'eventHubsName',
                    label: stringResources.AddTargetEventHub,
                    required: true,
                    isDataFlowExpression: true
                },
                formatSelection(false)
            ],
            enableImportSchemaInSink: true
        }
    ],
    controls: [
        {
            type: 'select-switch',
            bind: 'authMethod',
            label: stringResources.Authentication_Method_Label,
            options: [
                {
                    label: stringResources.AuthenticationType_AccountKey,
                    value: 'ConnectionString'
                }
            ],
            isLabelVertical: true,
            defaultValue: 'ConnectionString',
            cases: [
                {
                    caseValue: 'ConnectionString',
                    children: [
                        getConnectionSettings()
                    ]
                }
            ]
        }
    ]
};
